import { type Component } from 'solid-js';
import { useTranslation } from '@/i18n';

/**
 * Full-screen blocking modal shown when the game session ends (time runs out).
 * Cannot be dismissed - forces team to stop playing and go to the finish line.
 */
const GameEndedModal: Component = () => {
  const { t } = useTranslation();

  return (
    <div
      class="fixed inset-0 z-[9999] flex items-center justify-center"
      style="background: rgba(0, 0, 0, 0.95);"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="game-ended-title"
      aria-describedby="game-ended-description"
    >
      {/* Prevent any click-through */}
      <div
        class="absolute inset-0"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      />

      {/* Modal content */}
      <div class="relative z-10 max-w-lg mx-4 text-center">
        {/* Animated alarm icon */}
        <div class="mb-6">
          <div class="inline-block animate-pulse">
            <span class="text-8xl">⏰</span>
          </div>
        </div>

        {/* Main title */}
        <h1
          id="game-ended-title"
          class="text-5xl sm:text-6xl font-black text-red-500 mb-6 animate-pulse"
          style="text-shadow: 0 0 20px rgba(239, 68, 68, 0.5);"
        >
          {t('gameEnded.end')}
        </h1>

        {/* Message */}
        <div
          id="game-ended-description"
          class="space-y-4"
        >
          <p class="text-2xl sm:text-3xl font-bold text-white">
            {t('gameEnded.timeUp')}
          </p>

          <div class="bg-red-500/20 border-2 border-red-500/50 rounded-2xl p-6 mt-6">
            <p class="text-xl sm:text-2xl font-semibold text-red-300">
              {t('gameEnded.goToFinish')}
            </p>
          </div>

          <div class="bg-white/10 rounded-xl p-4 mt-6">
            <p class="text-lg text-white/80 flex items-center justify-center gap-2">
              <span class="text-2xl">📱</span>
              {t('gameEnded.moreInfoSms')}
            </p>
          </div>
        </div>

        {/* Pulsing border effect */}
        <div class="mt-8">
          <div class="inline-flex items-center gap-2 text-red-400/70 text-sm">
            <span class="w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
            <span>{t('gameEnded.gameFinished')}</span>
          </div>
        </div>
      </div>

      {/* Background animated elements */}
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          class="absolute top-1/4 left-1/4 w-32 h-32 bg-red-500/10 rounded-full blur-3xl animate-pulse"
          style="animation-delay: 0ms;"
        />
        <div
          class="absolute bottom-1/4 right-1/4 w-40 h-40 bg-red-500/10 rounded-full blur-3xl animate-pulse"
          style="animation-delay: 500ms;"
        />
        <div
          class="absolute top-1/2 right-1/3 w-24 h-24 bg-orange-500/10 rounded-full blur-3xl animate-pulse"
          style="animation-delay: 250ms;"
        />
      </div>
    </div>
  );
};

export default GameEndedModal;
